import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {MainLayoutComponent} from './components/smart/main-layout/main-layout.component';
import {UserAuthGuard} from './common/guards/user-auth.guard';
import {AuthLayoutComponent} from './auth/auth-layout/auth-layout.component';
import {NotificationsDrawerComponent} from './modules/notifications-drawer/notifications-drawer.component';
import {AgencyPortalAuthGuard} from './common/guards/agency-portal.guard';
import {UserRegulationAuthGuard} from './common/guards/user-regulation-auth.guard';
import {UserPermissionGuard} from './common/guards/user-permission.guard';
import {RegulationModeOnlyGuard} from './common/guards/regulation-mode-only.guard';
import {FixMyCodeAccessGuard} from './common/guards/fix-my-code-access.guard';
import {UserPermissions} from './common/elf-store/user-permissions/user-permissions.store';
// [NEW-GROUP] - disabled for ticket DASH-1168
// import {FeatureFlagGuard} from './common/guards/feature-flag.guard';
// import {FeatureFlagName} from './common/elf-store/feature-flag/feature-flag.store';
// import {PaymentLinkModule} from './modules/payment-link/payment-link.module';

const routes: Routes = [
  {
    path: 'login',
    component: AuthLayoutComponent,
    pathMatch: 'full',
    loadChildren: () => import('./auth/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'registration',
    component: AuthLayoutComponent,
    pathMatch: 'full',
    loadChildren: () => import('./auth/registration/registration.module').then(m => m.RegistrationModule),
  },
  {
    path: 'auth/exchange',
    component: AuthLayoutComponent,
    pathMatch: 'full',
    loadChildren: () => import('./auth/token-exchange/token-exchange.module').then(m => m.TokenExchangeModule),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [UserAuthGuard],
    canActivateChild: [RegulationModeOnlyGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'widget/my-sites/p/1',
      },
      {
        path: 'cc',
        pathMatch: 'full',
        redirectTo: 'settings/billing-invoices',
      },
      {
        path: 'embed-code',
        pathMatch: 'full',
        loadChildren: () => import('./modules/embed-code/embed-code.module').then(m => m.EmbedCodeModule),
      },
      {
        path: 'widget/:tab',
        redirectTo: 'widget/:tab/p/1',
      },
      {
        path: 'widget/:tab/p/:page',
        loadChildren: () => import('./modules/widget/widget.module').then(m => m.WidgetModule),
      },
      {
        path: 'scanner',
        canActivate: [UserPermissionGuard],
        data: {
          permissions: UserPermissions.manageScans.scansTab,
        },
        loadChildren: () => import('./modules/scanner/scanner.module').then(m => m.ScannerModule),
      },
      {
        path: 'audits',
        loadChildren: () => import('./modules/services/services.module').then(m => m.ServicesModule),
      },
      {
        path: 'vpat',
        loadChildren: () => import('./modules/services/services.module').then(m => m.ServicesModule),
      },
      {
        path: 'legal',
        loadChildren: () => import('./modules/services/services.module').then(m => m.ServicesModule),
      },
      {
        path: 'compliance',
        loadChildren: () => import('./modules/services/services.module').then(m => m.ServicesModule),
      },
      {
        path: 'video',
        loadChildren: () => import('./modules/services/services.module').then(m => m.ServicesModule),
      },
      {
        path: 'support-service',
        loadChildren: () =>
          import('./modules/support-services/support-services.module').then(m => m.SupportServicesModule),
      },
      {
        path: 'documentation',
        loadChildren: () =>
          import('./modules/support-services/support-services.module').then(m => m.SupportServicesModule),
      },
      {
        path: 'email-support',
        loadChildren: () =>
          import('./modules/support-services/support-services.module').then(m => m.SupportServicesModule),
      },
      {
        path: 'schedule-onboarding',
        loadChildren: () =>
          import('./modules/support-services/support-services.module').then(m => m.SupportServicesModule),
      },

      {
        path: 'image-alts',
        canActivate: [UserPermissionGuard],
        data: {
          permissions: UserPermissions.manageImageAlts.imageAltsTab,
        },
        loadChildren: () =>
          import('./modules/image-alts/image-alts-page/image-alts.module').then(m => m.ImageAltsModule),
      },
      {
        path: 'documents-and-pdfs',
        canActivate: [UserPermissionGuard],
        data: {
          permissions: UserPermissions.managePdfs.pdfsTab,
        },
        loadChildren: () => import('./modules/docs-and-pdfs/docs-and-pdfs.module').then(m => m.DocsAndPdfsModule),
      },
      {
        path: 'partners-and-agencies',
        loadChildren: () =>
          import('./modules/partners-and-agencies/partners-and-agencies.module').then(m => m.PartnersAndAgenciesModule),
      },
      {
        path: 'resources',
        loadChildren: () => import('./modules/resources/resources.module').then(m => m.ResourcesModule),
      },
      {
        path: 'team',
        canActivate: [UserPermissionGuard],
        data: {
          permissions: [
            UserPermissions.manageTeam.teamManagementPage.enforce2FAService,
            UserPermissions.manageTeam.teamManagementPage.editTeamService,
            UserPermissions.manageTeam.teamManagementPage.inviteTeamMemberService,
          ],
        },
        loadChildren: () => import('./modules/team/team.module').then(m => m.TeamModule),
      },
      // [NEW-GROUP] - disabled for ticket DASH-1168
      // {
      //   path: 'account-management',
      //   canActivate: [FeatureFlagGuard],
      //   data: {featureFlagName: FeatureFlagName.GROUPS_FEATURE},
      //   loadChildren: () =>
      //     import('./modules/account-management/account-management.module').then(m => m.AccountManagementModule),
      // },
      {
        path: 'settings',
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
      },
      {
        path: 'agency',
        canActivate: [AgencyPortalAuthGuard, UserPermissionGuard],
        data: {
          permissions: UserPermissions.manageAgencyResources.manageAgencyResourcesTab,
        },
        loadChildren: () => import('./modules/agency/agency.module').then(m => m.AgencyModule),
      },
      {
        path: 'regulation',
        canActivate: [UserRegulationAuthGuard, UserPermissionGuard],
        data: {
          permissions: UserPermissions.manageRegTracker.manageRegTrackerTab,
        },
        loadChildren: () => import('./modules/regulation/regulation.module').then(m => m.RegulationModule),
      },
      {
        path: 'notifications',
        pathMatch: 'full',
        component: NotificationsDrawerComponent,
        data: {
          openedFromUrl: true,
        },
      },
      {
        path: 'server-api',
        loadChildren: () => import('./modules/dev-resources/dev-resources.module').then(m => m.DevResourcesModule),
      },
      {
        path: 'fix-my-code',
        canActivate: [FixMyCodeAccessGuard],
        loadChildren: () => import('./modules/dev-resources/dev-resources.module').then(m => m.DevResourcesModule),
      },
    ],
  },
  {
    path: 'p',
    loadChildren: () => import('./modules/payment-link/payment-link.module').then(mod => mod.PaymentLinkModule),
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
